<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import EmailTagsInput from './EmailTagsInput.vue'
import clubInvitationService from '../services/clubInvitation.service'
import { useToastStore } from '../stores/toast'
import { isValidEmail } from '../utils/emailList'
import { useClubInvitationStatus } from '../composables/useClubInvitationStatus'
import type {
  ClubInvitationLookupItem,
  ClubInvitationSendResponse,
  EmailTagHint
} from '../types/clubInvitation.types'

const MAX_EMAILS = 200
const LOOKUP_DEBOUNCE_MS = 400

const props = defineProps<{
  show: boolean
  clubUuid: string
  signupEnabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'done', response: ClubInvitationSendResponse): void
  (e: 'signup-disabled'): void
}>()

const { t } = useI18n()
const toastStore = useToastStore()
const { formatDayMonth } = useClubInvitationStatus()

const emails = ref<string[]>([])
const submitting = ref(false)
const errorMessage = ref<string | null>(null)
const serverInvalid = ref<string[]>([])
// email → lookup result (null = nothing known about the address)
const lookupCache = ref<Record<string, ClubInvitationLookupItem | null>>({})
const tagsInput = ref<InstanceType<typeof EmailTagsInput> | null>(null)

let lookupTimer: ReturnType<typeof setTimeout> | null = null

const validEmails = computed(() => emails.value.filter(email => isValidEmail(email)))
const invalidCount = computed(() =>
  emails.value.filter(email => !isValidEmail(email) || serverInvalid.value.includes(email)).length
)

const knownItems = computed(() =>
  emails.value
    .map(email => lookupCache.value[email])
    .filter((item): item is ClubInvitationLookupItem => !!item)
)
const alreadyInvitedCount = computed(() => knownItems.value.filter(item => item.invited).length)
const alreadyMemberCount = computed(() => knownItems.value.filter(item => item.already_member).length)

const hints = computed<Record<string, EmailTagHint[]>>(() => {
  const result: Record<string, EmailTagHint[]> = {}
  for (const item of knownItems.value) {
    const list: EmailTagHint[] = []
    if (item.already_member) list.push({ label: t('clubInvitations.alreadyMember'), tone: 'warning' })
    if (item.invited) {
      const date = formatDayMonth(item.last_sent_at)
      list.push({ label: date ? t('clubInvitations.alreadySent', { date }) : t('clubInvitations.alreadySentNoDate'), tone: 'info' })
    }
    if (list.length) result[item.email] = list
  }
  return result
})

const canSubmit = computed(() =>
  !submitting.value &&
  props.signupEnabled !== false &&
  validEmails.value.length > 0 &&
  invalidCount.value === 0
)

const submitLabel = computed(() => {
  if (submitting.value) return t('clubInvitations.sending')
  if (invalidCount.value > 0) return t('clubInvitations.fixInvalid', { count: invalidCount.value })
  return t('clubInvitations.sendButton')
})

const runLookup = async () => {
  const pending = validEmails.value.filter(email => !(email in lookupCache.value))
  if (pending.length === 0 || !props.clubUuid) return
  try {
    const items = await clubInvitationService.lookup(props.clubUuid, pending)
    const next = { ...lookupCache.value }
    pending.forEach(email => { next[email] = null })
    items.forEach(item => { next[item.email] = item })
    lookupCache.value = next
  } catch (err) {
    // Markers are a hint only - sending still works without them
    console.error('Invitation lookup error:', err)
  }
}

watch(emails, (list) => {
  // Drop server errors for addresses that were edited or removed
  serverInvalid.value = serverInvalid.value.filter(email => list.includes(email))
  if (serverInvalid.value.length === 0) errorMessage.value = null

  if (lookupTimer) clearTimeout(lookupTimer)
  lookupTimer = setTimeout(runLookup, LOOKUP_DEBOUNCE_MS)
})

watch(() => props.show, (visible) => {
  if (!visible) return
  emails.value = []
  errorMessage.value = null
  serverInvalid.value = []
  lookupCache.value = {}
  nextTick(() => tagsInput.value?.focus())
})

const close = () => {
  if (!submitting.value) emit('close')
}

// Backdrop click must not throw away a typed list
const onBackdrop = () => {
  if (emails.value.length === 0) close()
}

const handleSubmit = async () => {
  tagsInput.value?.commit()
  await nextTick()
  if (!canSubmit.value) return

  submitting.value = true
  errorMessage.value = null
  const toSend = [...validEmails.value]

  try {
    const response = await clubInvitationService.send(props.clubUuid, toSend)
    if (response.processing_mode === 'sync' && (response.failed_count ?? 0) > 0) {
      toastStore.warning(response.message, 8000)
    } else {
      toastStore.success(response.message, response.processing_mode === 'async' ? 8000 : undefined)
    }
    emit('done', response)
  } catch (err: any) {
    console.error('Send invitations error:', err)
    const data = err.response?.data
    if (err.response?.status === 422 && data?.error === 'signup_disabled') {
      toastStore.error(data.message || t('clubInvitations.signupDisabledBanner'))
      emit('signup-disabled')
    } else if (data?.processing_mode === 'sync') {
      // Every mail failed - the invitations exist with "failed" status, so refresh the list
      toastStore.error(data.message || t('clubInvitations.errorSending'), 8000)
      emit('done', data)
    } else if (data?.errors) {
      serverInvalid.value = Object.keys(data.errors)
        .map(key => key.match(/^emails\.(\d+)$/))
        .filter((match): match is RegExpMatchArray => !!match)
        .map(match => toSend[Number(match[1])])
        .filter((email): email is string => !!email)
      errorMessage.value = data.message || t('clubInvitations.errorSending')
    } else {
      errorMessage.value = data?.message || t('clubInvitations.errorSending')
    }
  } finally {
    submitting.value = false
  }
}

onBeforeUnmount(() => {
  if (lookupTimer) clearTimeout(lookupTimer)
})
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="onBackdrop"
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-invitations-title"
    >
      <form class="p-6" @submit.prevent="handleSubmit">
        <h2 id="send-invitations-title" class="text-xl font-bold text-gray-900 mb-2">{{ t('clubInvitations.modalTitle') }}</h2>
        <p class="text-sm text-gray-600 mb-4">{{ t('clubInvitations.modalDescription') }}</p>

        <div
          v-if="signupEnabled === false"
          class="mb-4 p-3 bg-amber-50 border-2 border-amber-300 rounded text-sm text-amber-800"
        >
          {{ t('clubInvitations.signupDisabledBanner') }}
        </div>

        <label for="invitation-emails" class="block text-sm font-medium text-gray-700 mb-1">
          {{ t('clubInvitations.emailsLabel') }}
        </label>
        <EmailTagsInput
          ref="tagsInput"
          v-model="emails"
          input-id="invitation-emails"
          described-by="invitation-emails-hint"
          :max="MAX_EMAILS"
          :hints="hints"
          :invalid-emails="serverInvalid"
          :placeholder="t('clubInvitations.emailsPlaceholder')"
          :disabled="submitting"
        />
        <div class="mt-1 flex items-start justify-between gap-3">
          <p id="invitation-emails-hint" class="text-xs text-gray-500">{{ t('clubInvitations.emailsHint') }}</p>
          <span
            :class="['text-xs tabular-nums whitespace-nowrap', emails.length >= MAX_EMAILS ? 'text-amber-700 font-medium' : 'text-gray-500']"
            aria-live="polite"
          >
            {{ emails.length }} / {{ MAX_EMAILS }}
          </span>
        </div>

        <div v-if="alreadyInvitedCount > 0 || alreadyMemberCount > 0" class="mt-3 space-y-1">
          <p v-if="alreadyInvitedCount > 0" class="text-xs text-gray-600">
            {{ t('clubInvitations.alreadySentSummary', { count: alreadyInvitedCount }) }}
          </p>
          <p v-if="alreadyMemberCount > 0" class="text-xs text-amber-700">
            {{ t('clubInvitations.alreadyMemberSummary', { count: alreadyMemberCount }) }}
          </p>
        </div>

        <div v-if="errorMessage" class="mt-3 p-3 bg-red-50 border-2 border-red-300 rounded text-sm text-red-800" role="alert">
          {{ errorMessage }}
        </div>

        <div class="mt-6 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <button
            type="button"
            @click="close"
            :disabled="submitting"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            :disabled="!canSubmit"
            :class="[
              'px-4 py-2 text-sm font-medium text-white rounded flex items-center justify-center gap-2 disabled:cursor-not-allowed',
              invalidCount > 0 ? 'bg-red-600 disabled:opacity-80' : 'bg-green-600 hover:bg-green-700 disabled:opacity-50'
            ]"
          >
            <div v-if="submitting" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <svg v-else class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
            </svg>
            {{ submitLabel }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
